import useViewportSize from "@/hooks/useViewportSize";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useState } from "react";
import Popup from "@/components/popup/Popup";
import MobileSlider from "@/components/mobileSlider/MobileSlider";
import {
  IShopItem,
  IUserBalance,
  PurchaseStatusEnum,
  ShopItemStatus,
  ShopItemStatusEnum,
  ShopPurchaseErrorStatusEnum,
} from "../../types";
import { usePurchaseShopItem } from "../../hooks/useShop";
import ShopItemModalContent from "./ShopItemModalContent";
import { isActiveDiscount } from "../../helpers";

import styles from "./ShopItemModal.module.scss";

interface ShopItemModalProps {
  item: IShopItem | null;
  userBalanceData: IUserBalance[];
  isPopupOpen: boolean;
  setIsPopupOpen: (isOpen: boolean) => void;
}

const ShopItemModal: FC<ShopItemModalProps> = ({
  isPopupOpen,
  setIsPopupOpen,
  item,
  userBalanceData,
}) => {
  const [status, setStatus] = useState<ShopItemStatus>("idle");
  const purchaseShopItem = usePurchaseShopItem();
  const { width } = useViewportSize();
  const { t } = useTranslation();
  const isPurchasing = purchaseShopItem.isPending;

  const isBtnDisabled =
    isPurchasing ||
    purchaseShopItem?.error?.message === ShopPurchaseErrorStatusEnum.PURCHASE_LIMIT_REACHED ||
    status === ShopItemStatusEnum.PENDING;

  useEffect(() => {
    if (purchaseShopItem.isSuccess) {
      purchaseShopItem.reset();
      if (purchaseShopItem?.data?.status === PurchaseStatusEnum.SUCCESS) {
        setStatus(ShopItemStatusEnum.SUCCESS);
      } else if (purchaseShopItem?.data?.status === PurchaseStatusEnum.PENDING) {
        setStatus(ShopItemStatusEnum.PENDING);
      } else {
        setStatus(ShopItemStatusEnum.ERROR);
      }
    }

    if (purchaseShopItem.isError) {
      setStatus(ShopItemStatusEnum.ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseShopItem]);

  useEffect(() => {
    if (!isPopupOpen) {
      setStatus("idle");
      purchaseShopItem.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPopupOpen]);

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const handleBuy = () => {
    if (!item || isPurchasing) return;

    purchaseShopItem.mutate({
      itemId: item?.id,
      tokenId: item?.token.id,
      discountId: isActiveDiscount(item.activeDiscount) ? item.activeDiscount.id : undefined,
    });
  };

  const renderPopupContent = () => {
    return (
      <ShopItemModalContent
        status={status}
        item={item}
        userBalanceData={userBalanceData}
        isBtnDisabled={isBtnDisabled}
        isPurchasing={isPurchasing}
        handleBuy={handleBuy}
        handleClosePopup={handleClosePopup}
      />
    );
  };

  return (
    <>
      {width >= 940 ? (
        <Popup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          className={styles.popup}
          title={t("shop.purchaseItem", "Purchase Item")}
          headerClassName={styles.modalHeader}
          contentClassName={`${styles.popupContentWrapper} ${
            status === ShopItemStatusEnum.SUCCESS ? styles.successPopupContentWrapper : ""
          }`}
        >
          {renderPopupContent()}
        </Popup>
      ) : (
        <MobileSlider
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          className={styles.mobileSlider}
          title={t("shop.purchaseItem", "Purchase Item")}
          headerClassName={styles.modalHeader}
          contentClassName={`${styles.popupContentWrapper} ${
            status === ShopItemStatusEnum.SUCCESS ? styles.successPopupContentWrapper : ""
          }`}
        >
          {renderPopupContent()}
        </MobileSlider>
      )}
    </>
  );
};

export default ShopItemModal;
